import React, { useState, useEffect } from 'react';
import { FiSend, FiFileText, FiX, FiUser, FiUsers, FiPaperclip, FiSmile } from 'react-icons/fi';
import { toast } from 'react-toastify';
import EmojiPicker from './chat/EmojiPicker';
import '../css/CreateMessage.css';
import '../css/Chat.css';

const PRIORITIES = [
    { value: 'low', label: 'Alacsony', color: '#4caf50' },
    { value: 'normal', label: 'Normál', color: '#2196f3' },
    { value: 'high', label: 'Magas', color: '#ff9800' },
    { value: 'urgent', label: 'Sürgős', color: '#f44336' }
];

const CreateMessage = ({ onClose, users = { backend_users: [], client_users: [] }, onSend, onSaveDraft, draft = null, uploadAttachment = null }) => {
  const [form, setForm] = useState({
    subject: draft?.subject || '',
    content: draft?.content || '',
    priority: draft?.priority || 'normal',
    recipients: draft?.recipients || [],
    attachments: draft?.attachments || []
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedRecipients, setSelectedRecipients] = useState(
    draft?.recipients ? draft.recipients.map(r => ({
      userType: r.user_type,
      userId: r.user_type === 'backend' ? r.user_id : r.client_id,
      recipientType: r.recipient_type || 'to'
    })) : []
  );
  const [showRecipientDropdown, setShowRecipientDropdown] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showToDropdown, setShowToDropdown] = useState(false);
  const [showCcDropdown, setShowCcDropdown] = useState(false);
  const [toSearchTerm, setToSearchTerm] = useState('');
  const [ccSearchTerm, setCcSearchTerm] = useState('');

  useEffect(() => {
    if (draft) {
      // Check if this is a reply
      if (draft.isReply && draft.replyToMessage) {
        const replyMessage = draft.replyToMessage;
        

        let replyRecipient = null;
        if (replyMessage.from) {

          const backendUser = users.backend_users?.find(u => u.email === replyMessage.from);
          if (backendUser) {
            replyRecipient = {
              userType: 'backend',
              userId: backendUser.id,
              recipientType: 'to'
            };
          } else {

            const clientUser = users.client_users?.find(u => u.email === replyMessage.from);
            if (clientUser) {
              replyRecipient = {
                userType: 'client',
                userId: clientUser.id,
                recipientType: 'to'
              };
            }
          }
        }
        
        setForm({
          subject: `Re: ${replyMessage.subject || 'Üzenet'}`,
          content: `\n\n--- Eredeti üzenet ---\nFeladó: ${replyMessage.from || 'Ismeretlen'}\nDátum: ${replyMessage.date || replyMessage.created_at}\nTárgy: ${replyMessage.subject || 'Nincs tárgy'}\n\n${replyMessage.content || 'Nincs tartalom'}`,
          priority: replyMessage.priority || 'normal',
          recipients: replyRecipient ? [{
            user_type: replyRecipient.userType,
            user_id: replyRecipient.userType === 'backend' ? replyRecipient.userId : null,
            client_id: replyRecipient.userType === 'client' ? replyRecipient.userId : null,
            recipient_type: 'to'
          }] : [],
          attachments: []
        });
        setSelectedRecipients(replyRecipient ? [replyRecipient] : []);
      } else {

        setForm({
          subject: draft.subject || '',
          content: draft.content || '',
          priority: draft.priority || 'normal',
          recipients: draft.recipients || [],
          attachments: draft.attachments || []
        });
        setSelectedRecipients(
          draft.recipients ? draft.recipients.map(r => ({
            userType: r.user_type,
            userId: r.user_type === 'backend' ? r.user_id : r.client_id,
            recipientType: r.recipient_type || 'to'
          })) : []
        );
      }
    } else {
      setForm({
        subject: '',
        content: '',
        priority: 'normal',
        recipients: [],
        attachments: []
      });
      setSelectedRecipients([]);
    }
    setError('');
    setToSearchTerm('');
    setCcSearchTerm('');
  }, [draft]);

  const handleChange = e => {
    const { name, value, files } = e.target;
    if (name === 'attachments') {

      const fileArray = Array.from(files);

      const validFiles = fileArray.filter(file => {
        const allowedTypes = [
          'image/jpeg', 'image/png', 'image/gif', 'image/webp',
          'application/pdf', 'application/msword', 
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          'application/vnd.ms-excel',
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          'text/plain'
        ];
        
        if (!allowedTypes.includes(file.type)) {
          toast.error(`A ${file.name} fájl típusa nem támogatott`);
          return false;
        }
        
        if (file.size > 10 * 1024 * 1024) { // 10MB limit
          toast.error(`A ${file.name} fájl túl nagy (max 10MB)`);
          return false;
        }
        
        return true;
      });

      setForm(f => ({ ...f, attachments: [...f.attachments, ...validFiles] }));
    } else if (name === 'subject') {

      if (value.length <= 30) {
        setForm(f => ({ ...f, [name]: value }));
      }
    } else {
      setForm(f => ({ ...f, [name]: value }));
    }
  };

  const handleEmojiSelect = (emoji) => {
    setForm(prev => ({ ...prev, content: prev.content + emoji }));

  };

  const handleClose = () => {
    if (hasChanges()) {
      if (window.confirm('Van nem mentett változás a piszkozatban. Biztosan bezárod?')) {
        onClose();
      }
    } else {
      onClose();
    }
  };

  const addRecipient = (userType, userId, recipientType = 'to') => {
    const newRecipient = {
      user_type: userType,
      recipient_type: recipientType
    };

    if (userType === 'backend') {
      newRecipient.user_id = userId;
    } else {
      newRecipient.client_id = userId; // client_id alapján küldjük
    }

    setForm(f => ({
      ...f,
      recipients: [...f.recipients, newRecipient]
    }));

    setSelectedRecipients(prev => [...prev, { userType, userId, recipientType }]);

    if (recipientType === 'to') {
      setShowToDropdown(false);
      setToSearchTerm('');
    } else if (recipientType === 'cc') {
      setShowCcDropdown(false);
      setCcSearchTerm('');
    }
  };

  const removeRecipient = (index) => {
    setForm(f => ({
      ...f,
      recipients: f.recipients.filter((_, i) => i !== index)
    }));
    setSelectedRecipients(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    
    if (!form.subject || !form.content || form.recipients.length === 0) {
      setError('Minden kötelező mezőt tölts ki és válassz ki legalább egy címzettet!');
      return;
    }

    setError('');
    setLoading(true);

    try {

      const messageData = {
        ...form,
        attachments: []
      };


      const sendResult = await onSend(messageData);

      

      if (sendResult && sendResult.success && form.attachments.length > 0 && uploadAttachment) {
        const messageId = sendResult.data?.message_id || sendResult.data?.id;

        if (messageId) {

          for (const file of form.attachments) {
            try {
              const result = await uploadAttachment(file, messageId);

            } catch (error) {

              toast.error(`Hiba a ${file.name} feltöltésekor`);
            }
          }
        }
      }
      
      onClose();
    } catch (err) {
      setError(err.message || 'Hiba történt az üzenet küldésekor');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveDraft = async () => {
    if (!form.subject && !form.content) {
      setError('A piszkozat mentéséhez írj be legalább egy tárgyat vagy tartalmat!');
      return;
    }

    setError('');
    setLoading(true);

    try {

      const draftData = {
        ...form,
        attachments: []
      };

             const draftResult = await onSaveDraft(draftData);

       

       if (draftResult && draftResult.success && form.attachments.length > 0 && uploadAttachment) {
         const messageId = draftResult.data?.message_id || draftResult.data?.id;

         
         if (messageId) {

          for (const file of form.attachments) {
            try {
              const result = await uploadAttachment(file, messageId);

            } catch (error) {

              toast.error(`Hiba a ${file.name} feltöltésekor`);
            }
          }
        }
      }
      
      onClose();
    } catch (err) {
      setError(err.message || 'Hiba történt a piszkozat mentésekor');
    } finally {
      setLoading(false);
    }
  };


  const hasChanges = () => {
    if (!draft) return form.subject || form.content || form.recipients.length > 0;
    
    return (
      form.subject !== draft.subject ||
      form.content !== draft.content ||
      form.priority !== draft.priority ||
      JSON.stringify(form.recipients) !== JSON.stringify(draft.recipients)
    );
  };

  const isRecipientSelected = (userType, userId) => {
    return selectedRecipients.some(r => {
      if (r.userType === userType) {
        if (userType === 'backend') {
          return r.userId === userId;
        } else if (userType === 'client') {
          return r.userId === userId;
        }
      }
      return false;
    });
  };

  const filteredUsers = (searchTerm) => {
    const allUsers = [
      ...(users.backend_users || []).map(u => ({ ...u, user_type: 'backend' })),
      ...(users.client_users || []).map(u => ({ ...u, user_type: 'client' }))
    ];

    if (!searchTerm) {
      return allUsers;
    }

    return allUsers.filter(user =>
      user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (typeof user.company_name === 'string' && user.company_name.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  };

  // Dropdown bezárása kikattintással és ESC gombbal
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showToDropdown && !event.target.closest('.recipients-input-container')) {
        setShowToDropdown(false);
      }
      if (showCcDropdown && !event.target.closest('.recipients-input-container')) {
        setShowCcDropdown(false);
      }
      if (showEmojiPicker && !event.target.closest('.content-actions')) {
        setShowEmojiPicker(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        if (showToDropdown) {
          setShowToDropdown(false);
        }
        if (showCcDropdown) {
          setShowCcDropdown(false);
        }
        if (showEmojiPicker) {
          setShowEmojiPicker(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [showToDropdown, showCcDropdown]);

  const getRecipientDisplay = (recipient) => {
    let user;
    
    if (recipient.user_type === 'backend') {
      user = users.backend_users?.find(u => u.id === recipient.user_id);
    } else if (recipient.user_type === 'client') {
      // Client user-eket id alapján keresünk, mert az addRecipient client_id-ként az id-t állítja be
      user = users.client_users?.find(u => u.id === recipient.client_id);
    }
    
    if (user) {
      return {
        name: user.name || user.email,
        email: user.email,
        type: user.user_type || recipient.user_type,
        company: user.company_name
      };
    }
    return { name: 'Ismeretlen', email: 'unknown@example.com', type: 'unknown' };
  };

  return (
    <div className="create-message-container">
      <div className="create-message-header">
        <div className="create-message-title">
          <FiUser className="create-message-icon" />
          <span>{draft ? 'Piszkozat szerkesztése' : 'Új üzenet írása'}</span>
        </div>
        <button className="create-message-close" onClick={handleClose}>
          Vissza
        </button>
      </div>

      <form className="create-message-form" onSubmit={handleSubmit}>
        {/* Címzettek */}
        <div className="create-message-field">
          <div className="field-label">
            <span className="field-tag to-tag">{draft?.isReply ? 'Válasz' : 'To'}</span>
                         <div className="recipients-input-container">
               <input
                 type="text"
                 placeholder={draft?.isReply ? "Válasz címzett hozzáadása..." : "Címzett hozzáadása..."}
                 value={toSearchTerm}
                 onChange={(e) => setToSearchTerm(e.target.value)}
                 onFocus={() => setShowToDropdown(true)}
                 className="recipients-input"
                 style={{ colorScheme: 'auto' }}
               />
              
               {showToDropdown && (
                 <div className="recipients-dropdown">
                   <div className="dropdown-header">
                     <span>{draft?.isReply ? 'Válassz válasz címzettet' : 'Válassz címzettet'}</span>
                   </div>
                   
                   <div className="dropdown-search">
                     <input
                       type="text"
                       placeholder={draft?.isReply ? "Válasz címzett keresése..." : "Keresés..."}
                       value={toSearchTerm}
                       onChange={(e) => setToSearchTerm(e.target.value)}
                       className="dropdown-search-input"
                       style={{ colorScheme: 'auto' }}
                     />
                   </div>

                   <div className="dropdown-users">
                     {filteredUsers(toSearchTerm).map(user => (
                       <button
                         key={`${user.user_type}-${user.id}`}
                         type="button"
                         className={`dropdown-user ${isRecipientSelected(user.user_type, user.id) ? 'selected' : ''}`}
                         onClick={() => addRecipient(user.user_type, user.id, 'to')}
                         disabled={isRecipientSelected(user.user_type, user.id)}
                       >
                         <div className="user-name">{user.name || user.email}</div>
                         <div className="user-details">
                           {user.email} {user.company_name && `• ${user.company_name}`}
                         </div>
                         <div className="user-type-badge">
                           {user.user_type === 'backend' ? 'Backend' : 'Client'}
                         </div>
                       </button>
                     ))}
                   </div>
                 </div>
               )}
             </div>
          </div>

          {/* Kiválasztott TO címzettek */}
          {form.recipients.filter(r => r.recipient_type === 'to').length > 0 && (
            <div className="selected-recipients">
              {form.recipients.filter(r => r.recipient_type === 'to').map((recipient, index) => {
                const display = getRecipientDisplay(recipient);
                const actualIndex = form.recipients.findIndex(r => r === recipient);
                return (
                  <div key={`to-${index}`} className="selected-recipient">
                    <div className="recipient-info">
                      <span className="recipient-type">{recipient.recipient_type.toUpperCase()}</span>
                      <span className="recipient-name">{display.name}</span>
                      <span className="recipient-email">({display.email})</span>
                    </div>
                    <button 
                      type="button" 
                      className="remove-recipient"
                      onClick={() => removeRecipient(actualIndex)}
                    >
                      <FiX size={14} />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

                {/* CC */}
        <div className="create-message-field">
          <div className="field-label">
            <span className="field-tag cc-tag">Cc</span>
            <div className="recipients-input-container">
              <input
                type="text"
                placeholder={draft?.isReply ? "Másolat válasz címzett hozzáadása..." : "Másolat címzett hozzáadása..."}
                value={ccSearchTerm}
                onChange={(e) => setCcSearchTerm(e.target.value)}
                onFocus={() => setShowCcDropdown(true)}
                className="recipients-input"
                style={{ colorScheme: 'auto' }}
              />
              
              {showCcDropdown && (
                <div className="recipients-dropdown">
                  <div className="dropdown-header">
                    <span>{draft?.isReply ? 'Válassz válasz címzettet' : 'Válassz címzettet'}</span>
                  </div>
                  
                  <div className="dropdown-search">
                    <input
                      type="text"
                      placeholder={draft?.isReply ? "Válasz címzett keresése..." : "Keresés..."}
                      value={ccSearchTerm}
                      onChange={(e) => setCcSearchTerm(e.target.value)}
                      className="dropdown-search-input"
                      style={{ colorScheme: 'auto' }}
                    />
                  </div>

                  <div className="dropdown-users">
                    {filteredUsers(ccSearchTerm).map(user => (
                      <button
                        key={`${user.user_type}-${user.id}`}
                        type="button"
                        className={`dropdown-user ${isRecipientSelected(user.user_type, user.id) ? 'selected' : ''}`}
                        onClick={() => addRecipient(user.user_type, user.id, 'cc')}
                        disabled={isRecipientSelected(user.user_type, user.id)}
                      >
                        <div className="user-name">{user.name || user.email}</div>
                        <div className="user-details">
                          {user.email} {user.company_name && `• ${user.company_name}`}
                        </div>
                        <div className="user-type-badge">
                          {user.user_type === 'backend' ? 'Backend' : 'Client'}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Kiválasztott CC címzettek */}
          {form.recipients.filter(r => r.recipient_type === 'cc').length > 0 && (
            <div className="selected-recipients">
              {form.recipients.filter(r => r.recipient_type === 'cc').map((recipient, index) => {
                const display = getRecipientDisplay(recipient);
                const actualIndex = form.recipients.findIndex(r => r === recipient);
                return (
                  <div key={`cc-${index}`} className="selected-recipient">
                    <div className="recipient-info">
                      <span className="recipient-name">{display.name}</span>
                      <span className="recipient-email">({display.email})</span>
                    </div>
                    <button 
                      type="button" 
                      className="remove-recipient"
                      onClick={() => removeRecipient(actualIndex)}
                    >
                      <FiX size={14} />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Tárgy */}
        <div className="create-message-field">
          <div className="field-label">
            <input
              type="text"
              name="subject"
              value={form.subject}
              onChange={handleChange}
              placeholder={draft?.isReply ? "Válasz tárgy..." : "Tárgy hozzáadása..."}
              className="subject-input"
              maxLength={30}
              required
              style={{ colorScheme: 'auto' }}
            />
            <div className="subject-counter">
              {form.subject.length}/30
            </div>
          </div>
        </div>

        {/* Üzenet tartalom */}
        <div className="create-message-field">
          <div className="content-area">
                         <textarea
               name="content"
               value={form.content}
               onChange={e => {
                 if (e.target.value.length <= 2000) setForm(f => ({ ...f, content: e.target.value }));
               }}
               placeholder={draft?.isReply ? "Írd be a válaszod..." : "Írd be az üzeneted... / fájlok és egyéb tartalom beszúrásához"}
               className="content-textarea"
               rows={6}
               maxLength={2000}
               required
               style={{ colorScheme: 'auto' }}
             />
            <div className="content-footer">
                                           <div className="content-actions">
                <input 
                  type="file" 
                  name="attachments"
                  multiple
                  onChange={handleChange} 
                  id="content-file-upload"
                  className="file-input"
                />
                <label htmlFor="content-file-upload" className="content-action-btn" title="Csatolmány hozzáadása">
                  <FiPaperclip size={18} />
                </label>
                <button 
                  type="button" 
                  className="content-action-btn" 
                  title="Emoji hozzáadása"
                  onClick={() => setShowEmojiPicker(prev => !prev)}
                >
                  <FiSmile size={18} />
                </button>
                {showEmojiPicker && (
                  <EmojiPicker
                    onEmojiSelect={handleEmojiSelect}
                    onClose={() => {
                      
                    }}
                    isOpen={showEmojiPicker}
                    position="top"
                  />
                )}
              </div>
              <div className="content-counter">
                {form.content.length}/2000
              </div>
            </div>
          </div>
        </div>

        {/* Prioritás */}
        <div className="create-message-field">
          <div className="priority-section">
            <span className="priority-label">Prioritás:</span>
            <div className="priority-options">
              {PRIORITIES.map(p => (
                <button
                  key={p.value}
                  type="button"
                  className={`priority-option ${form.priority === p.value ? 'active' : ''}`}
                  onClick={() => setForm(f => ({ ...f, priority: p.value }))}
                  style={{ '--priority-color': p.color }}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Csatolmányok */}
        {form.attachments.length > 0 && (
          <div className="create-message-field">
            <div className="attachments-section">
              <span className="attachments-label">Csatolmányok:</span>
              <div className="attachments-list">
                {form.attachments.map((file, index) => (
                  <div key={index} className="attachment-item">
                    <FiFileText size={16} />
                    <span className="attachment-name">{file.name}</span>
                    <span className="attachment-size">
                      ({(file.size / 1024 / 1024).toFixed(2)} MB)
                    </span>
                    <button 
                      type="button" 
                      className="remove-attachment"
                      onClick={() => setForm(f => ({
                        ...f, 
                        attachments: f.attachments.filter((_, i) => i !== index)
                      }))}
                    >
                      <FiX size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Hibaüzenet */}
        {error && <div className="create-message-error">{error}</div>}

        {/* Műveletek */}
        <div className="create-message-actions">
          <div className="action-buttons">
            <button type="submit" className="send-button" disabled={loading}>
              <FiSend size={18} />
              {loading ? 'Küldés...' : 'Küldés'}
            </button>
            <button 
              type="button" 
              className={`draft-button ${hasChanges() ? 'has-changes' : ''}`} 
              onClick={handleSaveDraft} 
              disabled={loading || !hasChanges()}
            >
              <FiFileText size={18} />
              {loading ? 'Mentés...' : (draft ? 'Frissítés' : 'Piszkozat')}
            </button>
            <button type="button" className="cancel-button" onClick={handleClose}>
              Vissza
            </button>
          </div>
          
          
        </div>
      </form>
    </div>
  );
};

export default CreateMessage;
